import { useState } from 'react';
import clsx from 'clsx';

const types: Record<string, string> = {
  square: 'square-selector',
  rect: 'rect-selector',
  pill: 'pill-selector',
  'small-pill': 'small-pill-selector',
  checkbox: 'checkbox-selector',
  radio: 'radio-selector',
};

interface OptionConfig {
  value: string | number | boolean;
  img?: string;
  icon?: string;
  optionText: string;
  optionHTML?: string;
  subtext?: string;
  dataTestId?: string;
  dataAttributes?: Record<string, string>;
  [key: string]: unknown;
}

type SelectorOptions = Record<string, OptionConfig>;

type SelectorType = keyof typeof types;

interface OptionProps {
  selected: string | number | boolean | Array<string | number | boolean> | null;
  options: SelectorOptions;
  optionKey: string;
  onClick?: (value: OptionConfig['value']) => void | Promise<unknown>;
  type?: SelectorType;
  croppedImg?: boolean;
}

function Option({
  selected,
  options,
  optionKey,
  onClick,
  type,
  croppedImg,
}: OptionProps) {
  const [showSpinner, setShowSpinner] = useState(false);

  const handleClick = (optionValue: OptionConfig['value']) => {
    if (!onClick) return;
    const result = onClick(optionValue);
    if (result instanceof Promise) {
      setShowSpinner(true);
      result
        // eslint-disable-next-line no-console
        .catch((e: unknown) => console.log(e))
        .finally(() => setShowSpinner(false));
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    optionValue: OptionConfig['value']
  ) => {
    if (e.key === 'Enter') {
      handleClick(optionValue);
    }
  };

  if (!options[optionKey]) return null;

  const { value, img, icon, optionText, optionHTML, subtext } =
    options[optionKey];

  let isSelected: boolean | undefined;
  if (selected !== null) {
    isSelected = Array.isArray(selected)
      ? selected.includes(value)
      : value === selected;
  }

  const optionClasses = clsx(
    'selector-option',
    type != null ? types[type] : types['square'],
    { selected: isSelected }
  );

  const lowerCase = optionText.toLowerCase();
  const dataTestId = options[optionKey]['dataTestId'] || `option-${lowerCase}`;
  const dataAttributes = options[optionKey]['dataAttributes'] || {};

  return (
    <div
      role="button"
      tabIndex={0}
      {...dataAttributes}
      data-testid={dataTestId}
      aria-label={lowerCase}
      className={optionClasses}
      key={`${value}-option`}
      onClick={() => handleClick(value)}
      onKeyDown={(e) => handleKeyDown(e, value)}
    >
      {icon ? (
        <span
          aria-hidden
          className="icon"
          dangerouslySetInnerHTML={{ __html: icon }}
        ></span>
      ) : null}
      {img && !croppedImg ? (
        <img alt="" data-testid={`option-${lowerCase}-image`} src={img} />
      ) : null}
      {img && croppedImg ? (
        <div
          data-testid={`option-${lowerCase}-image`}
          className="cropped-img"
          style={{ backgroundImage: `url(${img})` }}
        ></div>
      ) : null}
      {optionHTML && (
        <div
          className="option-text"
          dangerouslySetInnerHTML={{ __html: optionHTML }}
        ></div>
      )}
      {optionText && !optionHTML ? (
        <div className="option-text">{optionText}</div>
      ) : null}
      {subtext ? (
        <div
          className="option-subtext"
          dangerouslySetInnerHTML={{ __html: subtext }}
        ></div>
      ) : null}
      {showSpinner ? <span className="hr-loading"></span> : null}
    </div>
  );
}

interface SelectorProps {
  selected: string | number | boolean | Array<string | number | boolean> | null;
  options: SelectorOptions;
  action?: (value: OptionConfig['value']) => void | Promise<unknown>;
  type?: SelectorType;
  optionsOrder?: Array<string | number>;
  croppedImg?: boolean;
}

/**
 * @deprecated
 */
function Selector({
  selected,
  options,
  action,
  type,
  optionsOrder,
  croppedImg,
}: SelectorProps) {
  const containerClasses = clsx('options-container', {
    pills: type && type.includes('pill'),
  });
  const optionsKeys = optionsOrder
    ? optionsOrder.map(String)
    : Object.keys(options);

  return (
    <div className={containerClasses}>
      {optionsKeys.map((key) => {
        return (
          <Option
            key={key}
            selected={selected}
            options={options}
            optionKey={key}
            onClick={action}
            type={type}
            croppedImg={croppedImg}
          />
        );
      })}
    </div>
  );
}

export default Selector;
